import React, { useState } from 'react';
import { useQuery } from 'react-query';
import axios from 'axios';
import { MessageSquare, Calendar, MapPin, User, Clock } from 'lucide-react';

const FeedbackList = () => {
  const [selectedFestival, setSelectedFestival] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const limit = 20;

  // Fetch festivals for filter dropdown
  const { data: festivalsData } = useQuery('festivals', async () => {
    const response = await axios.get('/api/festivals');
    return response.data;
  });

  // Fetch feedback
  const { data: feedbackData, isLoading, error } = useQuery(
    ['feedback', selectedFestival, currentPage],
    async () => {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: (currentPage * limit).toString()
      });
      
      if (selectedFestival) {
        params.append('festival_id', selectedFestival);
      }
      
      const response = await axios.get(`/api/feedback?${params}`);
      return response.data;
    },
    {
      keepPreviousData: true
    }
  );

  const festivals = festivalsData?.festivals || [];
  const feedback = feedbackData?.feedback || [];
  const pagination = feedbackData?.pagination || {};

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const formatLocation = (lat, lng) => {
    if (!lat || !lng) return 'No location';
    return `${parseFloat(lat).toFixed(4)}, ${parseFloat(lng).toFixed(4)}`;
  };

  const handleFestivalChange = (e) => {
    setSelectedFestival(e.target.value);
    setCurrentPage(0); // Reset to first page when changing filter
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  if (error) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <h2>Error Loading Feedback</h2>
        <p>{error.response?.data?.error || 'Failed to load feedback'}</p>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h2>User Feedback</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <MessageSquare size={20} />
          <span style={{ color: '#666' }}>
            {pagination.total || 0} total feedback submissions
          </span>
        </div>
      </div>

      {/* Filter Controls */}
      <div style={{
        background: 'white',
        padding: '20px',
        borderRadius: '8px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '20px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <label style={{ fontWeight: 'bold' }}>Filter by Festival:</label>
          <select
            value={selectedFestival}
            onChange={handleFestivalChange}
            style={{
              padding: '8px 12px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              minWidth: '200px'
            }}
          >
            <option value="">All Festivals</option>
            {festivals.map(festival => (
              <option key={festival.id} value={festival.id}>
                {festival.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Feedback List */}
      {isLoading ? (
        <div className="loading" style={{ padding: '40px', textAlign: 'center' }}>
          Loading feedback...
        </div>
      ) : feedback.length === 0 ? (
        <div style={{
          background: 'white',
          padding: '40px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          textAlign: 'center'
        }}>
          <MessageSquare size={48} style={{ color: '#ccc', marginBottom: '20px' }} />
          <h3>No Feedback Found</h3>
          <p style={{ color: '#666' }}>
            {selectedFestival ? 'No feedback has been submitted for this festival yet.' : 'No feedback has been submitted yet.'}
          </p>
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '15px' }}>
          {feedback.map(item => (
            <div key={item.id} style={{
              background: 'white',
              padding: '20px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
              border: '1px solid #e0e0e0'
            }}>
              {/* Header */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                marginBottom: '15px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Calendar size={16} style={{ color: '#666' }} />
                  <span style={{ fontWeight: 'bold' }}>
                    {item.festival_name || `Festival ID: ${item.festival_id}`}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: '#666', fontSize: '14px' }}>
                  <Clock size={14} />
                  {formatDate(item.submitted_at)}
                </div>
              </div>

              {/* Feedback Message */}
              <div style={{
                background: '#f8f9fa',
                padding: '15px',
                borderRadius: '6px',
                marginBottom: '15px',
                border: '1px solid #e9ecef'
              }}>
                <p style={{ margin: 0, lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                  {item.message}
                </p>
              </div>

              {/* Footer Info */}
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                fontSize: '14px',
                color: '#666'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <User size={14} />
                    <span>{item.user_identifier || 'Anonymous'}</span>
                  </div>
                  {(item.user_location_lat && item.user_location_lng) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <MapPin size={14} />
                      <span>{formatLocation(item.user_location_lat, item.user_location_lng)}</span>
                    </div>
                  )}
                </div>
                {item.user_agent && (
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    {item.user_agent.includes('Mobile') ? '📱 Mobile' : '💻 Desktop'}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {pagination.total > limit && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '10px',
          marginTop: '30px',
          padding: '20px'
        }}>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 0}
            className="btn btn-secondary"
            style={{
              opacity: currentPage === 0 ? 0.5 : 1,
              cursor: currentPage === 0 ? 'not-allowed' : 'pointer'
            }}
          >
            Previous
          </button>
          
          <span style={{ color: '#666' }}>
            Page {currentPage + 1} of {Math.ceil(pagination.total / limit)}
          </span>
          
          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={!pagination.hasMore}
            className="btn btn-secondary"
            style={{
              opacity: !pagination.hasMore ? 0.5 : 1,
              cursor: !pagination.hasMore ? 'not-allowed' : 'pointer'
            }}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default FeedbackList;
